import JoinCTA from '@/components/shared/JoinCTA';
import { PartnerLogo } from '@/components/shared/PartnerLogo';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import forbes_mobile from '@/static/assets/renters/renters_americas_best/forbes_mobile.svg';
import { PRODUCT_HOME_US_RENTERS } from '@/utils/products';
import { Flex, LegacyText } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useMemo } from 'react';
import styled from 'styled-components';

type StuffType = 'stuff_us' | 'stuff_fr' | 'stuff_renters_americas_best';

const DYNAMIC_STUFF = {
  stuff_us: dynamic(() => import('./MotionStuff').then(module => module.MotionStuff)),
  stuff_fr: dynamic(() => import('./MotionStuffFr').then(module => module.MotionStuffFr)),
  stuff_renters_americas_best: dynamic(() => import('./MotionStuffForbes').then(module => module.MotionStuffForbes)),
} as Record<StuffType, React.FC>;

const StyledTitle = styled(LegacyText)`
  white-space: normal;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre;
  `}
`;

const StyledPartnerLogo = styled(PartnerLogo)`
  margin-bottom: 0;
  ${MEDIA_QUERIES.desktop`
    margin-bottom: 40px;
  `}
`;

const ForbedBadgeMobile = styled(Image)`
  position: absolute;
  bottom: 0px;
  left: 0;
  right: 0;
  margin-inline: auto;
  z-index: 2;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}

  @media (min-height: 600px) and (max-height: 700px) {
    bottom: 70px;
  }
`;

export interface HeroProps {
  title: string;
  subtitle: string;
  ctaLabel: string;
  ctaUrl?: string;
  trackingLabel?: string;
  partnerLogo?: string;
  stuffType?: StuffType;
}

const MainCta: React.FC<React.PropsWithChildren<Pick<HeroProps, 'ctaUrl' | 'trackingLabel'>>> = ({
  children,
  trackingLabel,
  ctaUrl,
}) => {
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();

  return (
    <JoinCTA
      size="big"
      flavor="primary"
      glowing
      ref={mainCtaRef}
      data-source="renters"
      id="gtm_button_renters_check_prices"
      customUrl={ctaUrl}
      product={PRODUCT_HOME_US_RENTERS}
      minWidth={233}
      analyticsParams={
        trackingLabel
          ? {
              url: ctaUrl ?? '',
              position: 'header',
              landing_page_generator: 'true',
              original_product: PRODUCT_HOME_US_RENTERS,
              page_name: trackingLabel ?? '',
            }
          : {}
      }
    >
      {children}
    </JoinCTA>
  );
};

export const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaLabel,
  ctaUrl,
  trackingLabel = 'renters_hero',
  partnerLogo,
  stuffType = 'stuff_us',
}) => {
  const DynamicStuff = useMemo(() => DYNAMIC_STUFF[stuffType], [stuffType]);
  return (
    <Flex
      textAlign="center"
      flexDirection="column"
      alignItems="center"
      px={20}
      pb={partnerLogo ? 100 : 70}
      mb={[70, 100]}
      mt={[30, 100]}
      position="relative"
      minHeight={['600px', '600px', '450px']}
    >
      <DynamicStuff />

      {stuffType === 'stuff_renters_americas_best' && (
        <ForbedBadgeMobile src={forbes_mobile} alt="Renters America's Best by Forbed" />
      )}

      <Flex position="relative" flexDirection="column" alignItems="center" zIndex={2}>
        <StyledPartnerLogo src={partnerLogo} />
        <Flex flexDirection="column" alignItems="center" zIndex={0}>
          {
            <StyledTitle
              as="h1"
              type="alt"
              fontSize={['2.8rem', '5.5rem', '5.5rem', '5.5rem']}
              letterSpacing={[-0.25, 0, 0, 0]}
              lineHeight={[1.28, 1.17, 1.17, 1.17]}
              width={[1, 1, 1, 800]}
            >
              {parse(title)}
            </StyledTitle>
          }

          <LegacyText type="main" mt={[20, 20, 17, 17]} mb={[40, 40, 60, 60]} fontSize="2rem" lineHeight={[1.4]}>
            {parse(subtitle)}
          </LegacyText>

          <MainCta ctaUrl={ctaUrl} trackingLabel={trackingLabel}>
            {ctaLabel}
          </MainCta>
        </Flex>
      </Flex>
    </Flex>
  );
};
